import { useState, useEffect } from 'react';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';
import { bankOptions } from '@/mocks/profile';

interface Card {
  id: number;
  bank: string;
  cardType: string;
  cardNo: string;
  isDefault: boolean;
  theme: string;
}

const emptyForm = { bank: '', cardNo: '', holder: '', cardType: '储蓄卡' };

export default function BankPage() {
  const { showToast } = useToast();
  const [list, setList] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setList([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('bank_cards')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false });

      if (error) throw error;

      setList(
        (data || []).map((row) => ({
          id: row.id,
          bank: row.bank || '',
          cardType: row.card_type || '储蓄卡',
          cardNo: row.card_no || '',
          isDefault: row.is_default ?? false,
          theme: row.theme || 'from-gold-500 to-gold-800',
        })),
      );
    } catch {
      showToast('银行卡加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!form.bank.trim() || !form.cardNo.trim() || !form.holder.trim()) {
      showToast('请完整填写银行卡信息', 'error');
      return;
    }
    const masked = form.cardNo.replace(/\s/g, '');
    if (masked.length < 12 || masked.length > 19) {
      showToast('请输入正确的银行卡号', 'error');
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;

    const { error } = await supabase.from('bank_cards').insert({
      user_id: userId,
      bank: form.bank,
      card_type: form.cardType,
      card_no: `${masked.slice(0, 4)} **** **** ${masked.slice(-4)}`,
      is_default: list.length === 0,
      theme: 'from-gold-500 to-gold-800',
    });

    if (error) {
      showToast('银行卡添加失败', 'error');
      return;
    }
    setForm(emptyForm);
    setShowForm(false);
    showToast('银行卡添加成功');
    fetchData();
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    const { error } = await supabase.from('bank_cards').delete().eq('id', deletingId);
    if (error) {
      showToast('解绑失败', 'error');
    } else {
      showToast('银行卡已解绑');
    }
    setDeletingId(null);
    fetchData();
  };

  const setDefault = async (id: number) => {
    await supabase.from('bank_cards').update({ is_default: false }).neq('id', id);
    await supabase.from('bank_cards').update({ is_default: true }).eq('id', id);
    showToast('已设为默认卡');
    fetchData();
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      <SubHeader title="银行卡管理" />

      <div className="px-4 mt-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
          </div>
        ) : (
          list.map((card) => (
            <div
              key={card.id}
              className={`bg-gradient-to-br ${card.theme} rounded-2xl p-5 text-white shadow-sm relative overflow-hidden animate-fade-up`}
            >
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-base font-bold">{card.bank}</span>
                  <span className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-bank-card-2-line text-xl opacity-80"></i>
                  </span>
                </div>
                <p className="text-lg font-semibold tracking-widest mb-5">{card.cardNo}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-80">{card.cardType}</span>
                  {card.isDefault && (
                    <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">默认卡</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={() => setShowForm(true)}
            className="flex-1 py-3 rounded-xl border border-dashed border-gold-400 text-gold-600 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
          >
            添加银行卡
          </button>
        </div>

        {/* Management List */}
        {!loading && list.length > 0 && (
          <div className="bg-white rounded-2xl p-2 shadow-sm mt-2">
            {list.map((card, idx) => (
              <div
                key={card.id}
                className={`flex items-center justify-between px-3 py-3 ${
                  idx !== list.length - 1 ? 'border-b border-brown-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-gold-600">
                    <i className="ri-bank-card-line text-lg"></i>
                  </span>
                  <span className="text-brown-800 text-sm">{card.bank}</span>
                </div>
                <div className="flex items-center gap-4">
                  {card.isDefault ? (
                    <span className="text-gold-600 text-xs font-medium">默认卡</span>
                  ) : (
                    <button
                      onClick={() => setDefault(card.id)}
                      className="text-brown-400 text-xs cursor-pointer whitespace-nowrap"
                    >
                      设为默认
                    </button>
                  )}
                  <button
                    onClick={() => setDeletingId(card.id)}
                    className="text-brown-400 text-xs cursor-pointer whitespace-nowrap"
                  >
                    解绑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Card Sheet */}
      <BottomSheet open={showForm} onClose={() => setShowForm(false)} title="添加银行卡">
        <div className="px-4 pb-8 space-y-4">
          <div>
            <label className="block text-brown-600 text-sm mb-1.5">持卡人</label>
            <input
              type="text"
              value={form.holder}
              onChange={(e) => setForm({ ...form, holder: e.target.value })}
              placeholder="请输入持卡人姓名"
              className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-brown-600 text-sm mb-1.5">银行卡号</label>
            <input
              type="text"
              value={form.cardNo}
              onChange={(e) => setForm({ ...form, cardNo: e.target.value })}
              placeholder="请输入银行卡号"
              className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-brown-600 text-sm mb-1.5">开户银行</label>
            <select
              value={form.bank}
              onChange={(e) => setForm({ ...form, bank: e.target.value })}
              className="w-full text-sm text-brown-900 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors cursor-pointer"
            >
              <option value="">请选择开户银行</option>
              {bankOptions.map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-brown-600 text-sm mb-1.5">卡类型</label>
            <div className="flex gap-3">
              {['储蓄卡', '信用卡'].map((type) => (
                <button
                  key={type}
                  onClick={() => setForm({ ...form, cardType: type })}
                  className={`flex-1 py-3 rounded-xl border text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    form.cardType === type
                      ? 'border-gold-500 bg-gold-50 text-gold-700'
                      : 'border-brown-200 text-brown-500'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleSave}
            className="w-full py-3.5 rounded-full bg-gold-500 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform mt-2"
          >
            确认添加
          </button>
        </div>
      </BottomSheet>

      {/* Delete Confirm */}
      <BottomSheet open={deletingId !== null} onClose={() => setDeletingId(null)} title="解绑银行卡">
        <div className="px-4 pb-8">
          <p className="text-brown-600 text-sm text-center py-4">确定要解绑该银行卡吗？</p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeletingId(null)}
              className="flex-1 py-3 rounded-full border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              取消
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              解绑
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}